import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { SectionHeading } from "./primitives";
import { CONTAINER, SECTION_Y } from "./ui-tokens";

/**
 * A seção de solução da home: como é hoje × com o Ecclesiato.
 *
 * Ela junta duas seções que diziam a mesma coisa em sequência — o "antes e
 * depois" das quatro dores (pain-solution.tsx, que segue vivo na /about) e
 * este comparativo. Lidas uma depois da outra, a escala de 15 minutos
 * aparecia três vezes antes de a pessoa ver uma tela do produto.
 *
 * A coluna da esquerda é o cenário atual, não um concorrente nomeado: dá o
 * contraste sem afirmar nada sobre produto alheio que a gente não possa
 * sustentar. Só entra linha cujo lado direito o sistema já faz hoje.
 */
const rows = [
  {
    topic: "Visitante do domingo",
    before: "Ficha de papel que ninguém digita na segunda. No mês seguinte ele já está em outra igreja.",
    after: "Cadastro no pós-culto e jornada acompanhada do primeiro contato ao batismo",
  },
  {
    topic: "Reunião de célula",
    before: "Relatório cobrado líder por líder no WhatsApp",
    after: "Chamada registrada no celular em 30 segundos e um painel de qual célula cresce e qual esfria",
  },
  {
    topic: "Escala do mês",
    before: "Três a quatro horas na planilha, e ainda com furo no domingo",
    after: "15 minutos, respeitando indisponibilidade, culto preferido e limite por pessoa",
  },
  {
    topic: "Voluntário que não pode servir",
    before: "Mensagem solta no WhatsApp às 22h de sábado",
    after: "Ele marca no link antes de a escala existir",
  },
  {
    topic: "Relatório para a diretoria",
    before: "Alguém passa o fim de semana consolidando planilhas",
    after: "PDF pronto em um clique, com exportação para o contador",
  },
  {
    topic: "Quem enxerga o quê",
    before: "Todo mundo com acesso ao mesmo arquivo compartilhado",
    after: "Permissão por papel e registro de quem alterou o quê",
  },
  {
    topic: "Trazer a base para dentro",
    before: "A secretária redigitando mil e quinhentos cadastros",
    after: "A sua planilha do jeito que está, migrada pela nossa equipe",
  },
];

export default function Comparison() {
  const { trackNavClicked } = useDataFastGoals();
  const ref = useSectionViewGoal("comparison");

  return (
    <section
      ref={ref}
      id="solucao"
      aria-labelledby="solucao-titulo"
      className={`${SECTION_Y} bg-secondary/40`}
    >
      <div className={CONTAINER}>
        <SectionHeading
          id="solucao-titulo"
          title="A sua igreja já tem um sistema."
          muted="Ele só está espalhado em seis lugares."
        >
          Planilha, grupo de WhatsApp, caderno da secretaria, agenda do celular
          do pastor. Funciona, até a igreja crescer o bastante para que ninguém
          responda uma pergunta simples sem abrir quatro arquivos.
        </SectionHeading>

        {/* ── Desktop: tabela de verdade ──────────────────────────────── */}
        <div className="lp-reveal lp-surface mt-12 hidden overflow-hidden md:block">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">
              Como cada rotina funciona hoje e como fica com o Ecclesiato
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="w-[24%] px-6 py-4 font-medium text-muted-foreground">
                  Rotina
                </th>
                <th scope="col" className="w-[38%] px-6 py-4 font-medium text-muted-foreground">
                  Como é hoje
                </th>
                <th scope="col" className="w-[38%] px-6 py-4 font-semibold text-foreground">
                  Com o Ecclesiato
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr key={row.topic} className="align-top">
                  <th scope="row" className="px-6 py-5 font-semibold text-foreground">
                    {row.topic}
                  </th>
                  <td className="px-6 py-5 leading-relaxed text-muted-foreground">
                    {row.before}
                  </td>
                  <td className="px-6 py-5">
                    <span className="flex gap-2.5 leading-relaxed text-foreground">
                      <Check
                        className="mt-0.5 size-4 shrink-0 text-primary"
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                      {row.after}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Celular: um cartão por rotina ───────────────────────────── */}
        {/* A tabela de três colunas não cabe em 390px sem rolar de lado; aqui
            cada rotina se rotula sozinha. */}
        <ul className="mt-10 flex flex-col gap-3 md:hidden">
          {rows.map((row) => (
            <li key={row.topic} className="lp-reveal lp-surface p-5">
              <p className="font-semibold text-foreground">{row.topic}</p>
              <dl className="mt-3 flex flex-col gap-3 text-sm leading-relaxed">
                <div className="flex gap-2.5">
                  <dt className="sr-only">Como é hoje</dt>
                  <Minus
                    className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <dd className="text-muted-foreground">{row.before}</dd>
                </div>
                <div className="flex gap-2.5">
                  <dt className="sr-only">Com o Ecclesiato</dt>
                  <Check
                    className="mt-0.5 size-4 shrink-0 text-primary"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <dd className="text-foreground">{row.after}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>

        {/* Quem compara produto nomeado — e não o próprio jeito de fazer —
            sai daqui para /comparar, onde declaramos o que cada concorrente
            faz melhor. É um clique, não uma visualização: `trackNavClicked`,
            nunca `trackSectionViewed` (que sufixa `_viewed`). */}
        <div className="mt-8 flex flex-col gap-2 text-sm md:flex-row md:items-center md:justify-between">
          <p className="text-muted-foreground">
            Já usa outro sistema? A migração é igual: a base sai de lá em
            planilha e entra aqui pela nossa equipe.
          </p>
          <Link
            to="/comparar"
            onClick={() => trackNavClicked("comparison_to_comparar")}
            className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline hover:underline-offset-4"
          >
            Comparar com Eklesia, Enuves, inChurch, Voluts, Sige Igrejas ou célula.in
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
